import React, { Suspense, useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styles from './AudioPlayer.module.css';
import StoryShare, { StoryShareButton } from './StoryShare';
import {
  RiPlayFill,
  RiPauseFill,
  RiFileTextLine,
  RiLightbulbLine,
  RiMusicLine,
  RiMoonLine,
  RiArrowDownSLine,
  RiArrowUpSLine,
  RiCloseLine,
  RiLockLine,
} from 'react-icons/ri';
import { MdReplay30, MdForward30 } from 'react-icons/md';
import wasabiService from '../services/WasabiService';
import { getApiUrl } from '../services/apiConfig';
import StoryVerdict from './StoryVerdict';
import SRTViewer from './SRTViewer';
import { useAuth } from '../context/AuthContext';
import { useTutorial } from '../context/TutorialContext';
import lazyWithReload from '../utils/lazyWithReload';
import AutoBookCover, { splitTitleAndAuthor } from './AutoBookCover';
import BookFinishedCelebration from './BookFinishedCelebration';
import KnowledgePanel from './KnowledgePanel';
import { useOffline, bookKeyFor } from '../offline/OfflineContext';
import { getLocalPosition, saveLocalPosition } from '../offline/positions';
import { chapterAudioKeyCandidates } from '../offline/keys';
import { isIOS, labLog } from '../offline/platform';

// react-joyride only matters for the first-play tour; keep it out of the main bundle.
const AudioPlayerTutorial = lazyWithReload(() => import('./AudioPlayerTutorial'));

// Credit check interval (5 minutes)
const CREDIT_CHECK_INTERVAL = 5 * 60 * 1000;
const PLAYBACK_RATE_STORAGE_KEY = 'audioPlayerPlaybackRate';
// Per-book speed memory: a dense classic at 1x and a business book at 1.5x
// shouldn't fight over one global setting.
const PLAYBACK_RATE_BY_BOOK_KEY = 'audioPlayerRateByBook';
const SLEEP_PRESETS_MINUTES = [15, 30, 45, 60];

// Initial chunk size for audio loading (1MB)
const INITIAL_CHUNK_SIZE = 1 * 1024 * 1024;

const AUDIO_FILE_EXTENSIONS = ['mp3', 'm4a', 'wav', 'aac', 'ogg', 'flac'];
const AUDIOBOOK_TERMINAL_STATUSES = new Set(['ready', 'completed', 'failed']);
const PUBLIC_CATALOG_PREFIXES = [
  'curated/',
  'scientific_papers/',
  'graphic/',
  'classics/',
  'short_stories/',
  'explainers/'
];

const normalizeStatus = (status) => String(status || '').toLowerCase();
const normalizeCatalogRoot = (value) => {
  const candidate = String(value || '').trim().replace(/\/+$/, '');
  return candidate || 'curated';
};
const isPublicCatalogKey = (key) => {
  const normalized = String(key || '').trim();
  if (!normalized) return false;
  return PUBLIC_CATALOG_PREFIXES.some((prefix) => normalized.startsWith(prefix));
};

const isChapterLikelyPlayable = (chapter, completedChapters = 0) => {
  if (!chapter || typeof chapter !== 'object') return false;
  const chapterStatus = normalizeStatus(chapter.status);
  if (['ready', 'completed', 'tts_complete', 'uploading'].includes(chapterStatus)) {
    return true;
  }
  if (chapter.audioUrl || chapter.audio_url || chapter.audioKey || chapter.audio_key) {
    return true;
  }
  const completed = Number(completedChapters || 0);
  if (completed <= 0) return false;
  const order = Number(chapter.order || chapter.chapter_order || 0);
  const chapterIdNumber = Number(chapter.id);
  return (
    (Number.isFinite(order) && order > 0 && order <= completed) ||
    (Number.isFinite(chapterIdNumber) && chapterIdNumber > 0 && chapterIdNumber <= completed)
  );
};

const toWasabiKey = (value) => {
  if (!value || typeof value !== 'string') {
    return '';
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }

  if (trimmed.startsWith('users/') || isPublicCatalogKey(trimmed)) {
    return trimmed;
  }

  const proxiedMarker = '/api/wasabi/file/';
  if (trimmed.includes(proxiedMarker)) {
    const proxiedPart = trimmed.split(proxiedMarker)[1] || '';
    const cleanPart = proxiedPart.split('?')[0];
    try {
      return decodeURIComponent(cleanPart);
    } catch (_) {
      return cleanPart;
    }
  }

  try {
    const parsed = new URL(trimmed);
    const key = decodeURIComponent((parsed.pathname || '').replace(/^\/+/, ''));
    return key;
  } catch (_) {
    const withoutQuery = trimmed.split('?')[0];
    const marker = '.com/';
    if (withoutQuery.includes(marker)) {
      return decodeURIComponent(withoutQuery.split(marker)[1] || '');
    }
    return withoutQuery;
  }
};

const uniqueKeys = (values) => {
  const seen = new Set();
  const results = [];
  values.forEach((value) => {
    const key = toWasabiKey(value);
    if (!key || seen.has(key)) {
      return;
    }
    seen.add(key);
    results.push(key);
  });
  return results;
};

const looksLikeSrt = (content) => (
  typeof content === 'string' &&
  /\d{2}:\d{2}:\d{2},\d{3}\s+-->\s+\d{2}:\d{2}:\d{2},\d{3}/.test(content)
);

const isKeyForCurrentAudiobook = (key, {
  isCurated,
  userId,
  audiobookId,
  sourceAudiobookId,
  catalogRoot
}) => {
  if (!key || !audiobookId) {
    return false;
  }

  const effectiveSourceId = String(sourceAudiobookId || audiobookId);
  const effectiveCatalogRoot = normalizeCatalogRoot(catalogRoot);
  const expectedPrefix = isCurated
    ? `${effectiveCatalogRoot}/audiobooks/${effectiveSourceId}/`
    : `users/${userId}/audiobooks/${audiobookId}/`;

  return key.startsWith(expectedPrefix);
};

function AudioPlayer({ audioFile, userId, onPlaybackStateChange, onAddAnotherBook, onOpenCuratedBook, onClose, autoMinimize = false, subscriptionActive = false }) {
  const audioRef = useRef(null);
  const playerContainerRef = useRef(null);
  const lastCreditCheckRef = useRef(0);
  const { currentUser, getToken } = useAuth();
  const { hasCompletedPlayerTutorial, startPlayerTour, runPlayerTour } = useTutorial();
  // Cinematic already mixes its own score/beds into the master; layering the
  // client's ambient-loop picker on top would double up the music.
  // A Story Time series is scored and mixed like a cinematic book: the ambient-music picker must not play over it.
  const isCinematicAudio = audioFile?.voiceTier === 'cinematic' || audioFile?.voiceTier === 'story_time' || audioFile?.documentType === 'story_time';

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showTranscript, setShowTranscript] = useState(false);
  // Knowledge card: probe once per book so the bulb only appears for books
  // that actually have a card.
  const [hasKnowledgeCard, setHasKnowledgeCard] = useState(false);
  const [showKnowledge, setShowKnowledge] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(() => {
    try {
      const parsed = Number(localStorage.getItem(PLAYBACK_RATE_STORAGE_KEY));
      const allowed = [0.5, 0.75, 1.0, 1.25, 1.5, 2.0];
      return allowed.includes(parsed) ? parsed : 1.0;
    } catch (_) {
      return 1.0;
    }
  });
  const [showSpeedControls, setShowSpeedControls] = useState(false);
  // Sleep timer: a minutes preset (wall clock) or 'chapter' (stop after the
  // current chapter ends). Bedtime listening table stakes.
  const [sleepMode, setSleepMode] = useState(null); // null | minutes | 'chapter'
  const [sleepEndsAt, setSleepEndsAt] = useState(null);
  const [sleepRemaining, setSleepRemaining] = useState(0);
  const [showSleepMenu, setShowSleepMenu] = useState(false);
  const sleepModeRef = useRef(null);
  const [showChapters, setShowChapters] = useState(false);
  const [srtContent, setSrtContent] = useState(null);
  const [wordTimestamps, setWordTimestamps] = useState([]);
  const [currentChapter, setCurrentChapter] = useState(null);
  const [resumeChapterId, setResumeChapterId] = useState(null);
  const [resumePosition, setResumePosition] = useState(0);
  const [initialChapterPosition, setInitialChapterPosition] = useState(0);
  const [audioToken, setAudioToken] = useState('');
  const [audioCandidateIndex, setAudioCandidateIndex] = useState(0);
  // blob: URL of the on-device chapter file when the book is downloaded.
  const [offlineSrc, setOfflineSrc] = useState('');
  const offline = useOffline();
  const offlineBookKey = audioFile?.id ? bookKeyFor(audioFile.id, Boolean(audioFile?.isCurated)) : '';

  const [isBuffering, setIsBuffering] = useState(false);
  const [bufferedRanges, setBufferedRanges] = useState([]);
  const [bookmarks, setBookmarks] = useState([]);
  const [showBookmarks, setShowBookmarks] = useState(false);
  // Story Time creations stream here and are shared by link; no file download (the server refuses it too).
  const isStoryTime = audioFile?.voiceTier === 'story_time' || audioFile?.documentType === 'story_time';
  // Story Time ratings: the owner is asked after episode 1 and after the last episode, and each finished episode is counted (story_time_share_routes.py).
  const [storyAsk, setStoryAsk] = useState(''); const storyAsked = useRef({});
  const storyPost = async (path, body) => {
    try { const token = await getToken(); await fetch(getApiUrl(`/api/story-time/jobs/${encodeURIComponent(audioFile.id)}/${path}`), { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(body) }); } catch (e) { /* never worth an error on the player */ }
  };
  const [downloadingChapterId, setDownloadingChapterId] = useState(null);
  // Story Time Premium: a curated series whose episodes after the first need a
  // subscription. The backend gate (main.py _assert_premium_catalog_access) is
  // the real wall; this only draws the locks and the upgrade note so a free
  // listener never hits a silent failed load. The full-book record carries
  // the manifest's `premium` flag; the shelf card carries `isPremium`.
  const isPremiumCurated = Boolean(
    audioFile?.isCurated && (
      audioFile?.isPremium || audioFile?.premium ||
      String(audioFile?.catalogRoot || '') === 'story_time_premium'
    )
  );
  const isEpisodeLocked = (index) => isPremiumCurated && index > 0 && !subscriptionActive;
  // Index of the locked episode the listener just asked for (shows the note).
  const [lockedEpisode, setLockedEpisode] = useState(null);

  // Background music state
  const [currentMusicTrack, setCurrentMusicTrack] = useState(-1);
  const [isMusicPlaying, setIsMusicPlaying] = useState(false);
  const [showMusicMenu, setShowMusicMenu] = useState(false);
  const [bgMusicVolume, setBgMusicVolume] = useState(0.05); // Default to 5%
  const [transcriptPlayerClearance, setTranscriptPlayerClearance] = useState(0);
  const [isCompact, setIsCompact] = useState(false);
  // Mobile layout: a docked mini-player above the bottom nav that expands
  // into a full-screen sheet with the complete feature set.
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= 768
  );
  const [isExpanded, setIsExpanded] = useState(false);
  const [miniCoverFailed, setMiniCoverFailed] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  // Reading-list context for the celebration: {listTitle, finishedCount,
  // total, levelTitle, nextBook} when the finished book belongs to a list.
  const [celebrationListProgress, setCelebrationListProgress] = useState(null);
  const [resolvedCoverSrc, setResolvedCoverSrc] = useState('');
  // Measured height of the bottom tab bar so the mini-player docks flush
  // against it (the nav's height varies with font rendering).
  const [navClearance, setNavClearance] = useState(56);
  const bgMusicRef = useRef(null);
  const curatedCatalogRoot = normalizeCatalogRoot(audioFile?.catalogRoot);
  const curatedSourceAudiobookId = String(audioFile?.sourceAudiobookId || audioFile?.id || '');

  // Resolve the cover into a loadable URL. The full-book objects the player
  // receives usually carry a storage key or backend-file reference (not a
  // presigned URL), which cannot be used directly as an <img src>.
  useEffect(() => {
    let cancelled = false;
    let objectUrl = '';

    const loadCover = async () => {
      const raw = String(audioFile?.coverUrl || '').trim();

      // Offline: the downloaded record carries the cover image.
      const storedCover = offlineBookKey ? offline.records?.[offlineBookKey]?.coverBlob : null;
      if (storedCover && !offline.online) {
        objectUrl = URL.createObjectURL(storedCover);
        setResolvedCoverSrc(objectUrl);
        return;
      }

      // Already a presigned/absolute storage URL — loadable as-is.
      if (raw.includes('X-Amz-')) {
        setResolvedCoverSrc(raw);
        return;
      }

      // Curated books have a public cover endpoint (no auth needed).
      if (audioFile?.isCurated && audioFile?.id) {
        setResolvedCoverSrc(getApiUrl(`/api/curated-cover/${encodeURIComponent(audioFile.id)}`));
        return;
      }

      if (!raw) {
        setResolvedCoverSrc('');
        return;
      }

      try {
        const resolved = await wasabiService.resolveCoverImageSource(raw);
        if (cancelled) {
          if (resolved?.shouldRevoke && resolved?.src) URL.revokeObjectURL(resolved.src);
          return;
        }
        if (resolved?.shouldRevoke) objectUrl = resolved.src;
        setResolvedCoverSrc(resolved?.src || '');
      } catch (error) {
        if (!cancelled) setResolvedCoverSrc('');
      }
    };

    setResolvedCoverSrc('');
    loadCover();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioFile?.id, audioFile?.coverUrl, audioFile?.isCurated, offline.online, offlineBookKey]);

  useEffect(() => {
    const measurePlayerClearance = () => {
      if (!playerContainerRef.current || typeof window === 'undefined') {
        setTranscriptPlayerClearance(0);
        document.body.style.setProperty('--player-clearance', '0px');
        return;
      }
      // Compute from layout properties (not getBoundingClientRect) so the
      // open/close transform animation can't skew the measurement.
      const el = playerContainerRef.current;
      const computed = window.getComputedStyle(el);
      const bottomOffset = parseFloat(computed.bottom) || 0;
      const marginBottom = parseFloat(computed.marginBottom) || 0;
      const clearance = Math.max(0, el.offsetHeight + bottomOffset + marginBottom + 20);
      setTranscriptPlayerClearance(Math.round(clearance));
      document.body.style.setProperty('--player-clearance', `${Math.round(clearance)}px`);
    };

    measurePlayerClearance();
    window.addEventListener('resize', measurePlayerClearance);
    return () => window.removeEventListener('resize', measurePlayerClearance);
  }, [showTranscript, showChapters, showMusicMenu, currentChapter?.id, duration, isCompact, isMobile, isExpanded]);

  useEffect(() => () => {
    document.body.style.removeProperty('--player-clearance');
  }, []);

  // Collapse to the compact pill while an overlay (e.g. the upload wizard)
  // is open, and restore the previous state only if we were the ones who
  // minimized it.
  const wasAutoMinimizedRef = useRef(false);
  useEffect(() => {
    if (autoMinimize) {
      setIsCompact((previous) => {
        if (!previous) wasAutoMinimizedRef.current = true;
        return true;
      });
    } else if (wasAutoMinimizedRef.current) {
      wasAutoMinimizedRef.current = false;
      setIsCompact(false);
    }
  }, [autoMinimize]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!isMobile || typeof document === 'undefined') return undefined;
    const measureNav = () => {
      const nav = document.querySelector('[class*="bottomNav"]');
      if (nav) {
        // The dock floats above the bottom edge (12px + safe-area inset), so
        // the clearance is the gap from the dock's top to the viewport
        // bottom, plus a little air. Its bare height left the mini player
        // sitting on top of it on notched phones.
        const rect = nav.getBoundingClientRect();
        const clearance = Math.ceil(window.innerHeight - rect.top) + 8;
        if (rect.height > 0 && clearance > 0 && clearance < 240) {
          setNavClearance(clearance);
        }
      }
    };
    measureNav();
    window.addEventListener('resize', measureNav);
    return () => window.removeEventListener('resize', measureNav);
  }, [isMobile]);

  // The player tour targets live inside the expanded sheet on mobile, so
  // open it while the tour is running.
  useEffect(() => {
    if (isMobile && runPlayerTour) {
      setShowTranscript(false);
      setIsExpanded(true);
    }
  }, [isMobile, runPlayerTour]);

  // Lock page scroll behind the full-screen mobile sheet.
  useEffect(() => {
    if (!isExpanded || typeof document === 'undefined') return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previousOverflow; };
  }, [isExpanded]);

  useEffect(() => {
    setMiniCoverFailed(false);
    setShowCelebration(false);
  }, [audioFile?.id]);

  useEffect(() => {
    if (!isCompact) return;
    setShowTranscript(false);
    setShowChapters(false);
    setShowSpeedControls(false);
    setShowMusicMenu(false);
  }, [isCompact]);

  useEffect(() => {
    if (onPlaybackStateChange) {
      onPlaybackStateChange(isPlaying);
    }
    if (isPlaying) offline.noteListened();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying, onPlaybackStateChange]);

  // Report listening time (for usage insights) while playing.
  useEffect(() => {
    let cancelled = false;
    setHasKnowledgeCard(false);
    setShowKnowledge(false);
    const probe = async () => {
      try {
        const token = await getToken();
        if (!token || !audioFile?.id) return;
        const res = await fetch(
          getApiUrl(`/api/knowledge/${encodeURIComponent(audioFile.id)}`),
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!cancelled && res.ok) {
          const data = await res.json();
          setHasKnowledgeCard(Boolean(data.hasCard));
        }
      } catch (e) { /* card is a bonus; stay quiet */ }
    };
    probe();
    return () => { cancelled = true; };
  }, [audioFile?.id, getToken]);

  useEffect(() => {
    if (!isPlaying) return undefined;
    const bookId = audioFile?.id;
    let lastTs = Date.now();
    const report = async (secs) => {
      if (!secs || secs <= 0) return;
      try {
        const token = await getToken();
        if (!token) return;
        await fetch(getApiUrl('/api/listening/event'), {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ audiobookId: bookId, seconds: secs }),
          keepalive: true,
        });
      } catch (e) {
        /* non-critical: ignore listening-report failures */
      }
    };
    const interval = setInterval(() => {
      const now = Date.now();
      const delta = Math.round((now - lastTs) / 1000);
      lastTs = now;
      report(Math.min(delta, 60));
    }, 30000);
    return () => {
      clearInterval(interval);
      report(Math.min(Math.round((Date.now() - lastTs) / 1000), 60));
    };
  }, [isPlaying, audioFile?.id, getToken]);

  // Cinematic books never get the client-side ambient loop: force it off and
  // close the picker if the viewer had one running before switching books.
  useEffect(() => {
    if (!isCinematicAudio) return;
    if (bgMusicRef.current) {
      bgMusicRef.current.pause();
      bgMusicRef.current.currentTime = 0;
    }
    setIsMusicPlaying(false);
    setCurrentMusicTrack(-1);
    setShowMusicMenu(false);
  }, [isCinematicAudio]);

  // Background music tracks
  const bgMusicTracks = [
    { name: 'Ambient 1', file: '/background-music/ambient1.mp3' },
    { name: 'Ambient 2', file: '/background-music/ambient2.mp3' },
    { name: 'Ambient 3', file: '/background-music/ambient3.mp3' },
    { name: 'Happy Beat', file: '/background-music/happy-beat.mp3' },
    { name: 'Inspirational', file: '/background-music/inspirational-1.mp3' },
    { name: 'Lo-Fi Beat', file: '/background-music/lofi-beat.mp3' },
    { name: 'Lo-Fi Memories', file: '/background-music/lofi-old-memories.mp3' },
    { name: 'Sensual Upbeat', file: '/background-music/sensual-upbeat.mp3' },
    { name: 'Suspense Builder', file: '/background-music/suspense-builder.mp3' },
    { name: 'Suspense Droning', file: '/background-music/suspense-droning.mp3' }
  ];

  // Set initial chapter when audioFile changes (book-level resume for all content).
  useEffect(() => {
    let cancelled = false;

    const prepareInitialChapter = async () => {
      if (!audioFile?.chapters?.length) {
        return;
      }

      const firstChapter = audioFile.chapters[0];
      const completedChapters = Number(audioFile?.completedChapters || 0);
      const firstPlayableChapter = audioFile.chapters.find((chapter) => (
        isChapterLikelyPlayable(chapter, completedChapters)
      )) || firstChapter;
      const audiobookIsInProgress = !AUDIOBOOK_TERMINAL_STATUSES.has(normalizeStatus(audioFile?.status));

      try {
        // Server state when reachable; the on-device mirror wins when it is
        // newer (a listen that happened offline) or the network is away.
        const localState = await getLocalPosition(audioFile.id);
        // Resume from the on-device position straight away; the server
        // round trip (Render, ~0.5 s or more on a phone) only refines it.
        if (localState?.lastChapterId && !cancelled) {
          const quickChapter = audioFile.chapters.find(ch => String(ch?.id) === String(localState.lastChapterId));
          if (quickChapter) {
            setResumeChapterId(String(quickChapter.id));
            setResumePosition(Math.max(0, parseFloat(localState.lastPlayedPosition || 0)));
            setCurrentChapter(quickChapter);
          }
        }
        let playbackState = null;
        try {
          playbackState = await wasabiService.getAudiobookPlaybackState(audioFile.id);
        } catch (remoteError) {
          if (!localState) throw remoteError;
        }
        const remoteUpdatedAt = Date.parse(playbackState?.updatedAt || playbackState?.lastPlayedAt || '') || 0;
        if (localState && (!playbackState || localState.pending || localState.updatedAt > remoteUpdatedAt)) {
          playbackState = { ...(playbackState || {}), ...localState };
        }
        const chapterId = playbackState?.lastChapterId ? String(playbackState.lastChapterId) : null;
        const lastPosition = Math.max(0, parseFloat(playbackState?.lastPlayedPosition || 0));
        const rawMatchingChapter = chapterId
          ? audioFile.chapters.find(ch => String(ch?.id) === chapterId)
          : null;
        const matchingChapter = rawMatchingChapter && (
          !audiobookIsInProgress || isChapterLikelyPlayable(rawMatchingChapter, completedChapters)
        )
          ? rawMatchingChapter
          : null;

        // The user may already be listening from the on-device position;
        // do not yank them to the server's answer.
        const alreadyPlaying = Boolean(audioRef.current && !audioRef.current.paused);
        if (!cancelled && !alreadyPlaying) {
          setResumeChapterId(matchingChapter ? String(matchingChapter.id) : null);
          setResumePosition(matchingChapter ? lastPosition : 0);
          setCurrentChapter(matchingChapter || firstPlayableChapter);
        }
      } catch (error) {
        console.error('Error loading audiobook playback state:', error);
        if (!cancelled) {
          setResumeChapterId(null);
          setResumePosition(0);
          setCurrentChapter(firstPlayableChapter);
        }
      }
    };

    prepareInitialChapter();

    return () => {
      cancelled = true;
    };
  }, [
    audioFile?.id,
    audioFile?.isCurated,
    audioFile?.status,
    audioFile?.completedChapters,
    audioFile?.chapters,
    audioFile?.chapters?.length
  ]);


  // Start tutorial for first-time audio player users
  useEffect(() => {
    if (!hasCompletedPlayerTutorial && audioFile) {
      const timer = setTimeout(() => {
        startPlayerTour();
      }, 2000); // 2 second delay to let player load
      
      return () => clearTimeout(timer);
    }
  }, [hasCompletedPlayerTutorial, audioFile, startPlayerTour]);

  // Safari: mark this as long-form playback so it keeps going with the
  // screen locked and mixes like a media app.
  useEffect(() => {
    try {
      if (navigator.audioSession && navigator.audioSession.type !== 'playback') {
        navigator.audioSession.type = 'playback';
      }
    } catch (e) { /* unsupported */ }
  }, []);

  // Downloaded chapter: play the local file. Disk-backed blob URLs seek
  // natively; no service worker sits in the audio path.
  const currentChapterIdForOffline = currentChapter?.id;
  const offlineChapterReady = Boolean(
    offlineBookKey && currentChapterIdForOffline && offline.isChapterAvailable(offlineBookKey, currentChapterIdForOffline)
  );
  useEffect(() => {
    let cancelled = false;
    let objectUrl = '';
    if (!offlineChapterReady) {
      setOfflineSrc('');
      return undefined;
    }
    offline.getChapterFile(offlineBookKey, currentChapterIdForOffline).then((file) => {
      if (cancelled || !file) {
        if (!cancelled) setOfflineSrc('');
        return;
      }
      objectUrl = URL.createObjectURL(file);
      setOfflineSrc(objectUrl);
    }).catch(() => { if (!cancelled) setOfflineSrc(''); });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offlineChapterReady, offlineBookKey, currentChapterIdForOffline, offline.records]);

  useEffect(() => {
    let cancelled = false;

    const refreshAudioToken = async () => {
      // Free catalog streams need no token; premium episodes carry it on the
      // stream URL the same way private books do.
      if (audioFile?.isCurated && !isPremiumCurated) {
        if (!cancelled) {
          setAudioToken('');
        }
        return;
      }

      if (!currentUser || !getToken) {
        if (!cancelled) {
          setAudioToken('');
        }
        return;
      }

      try {
        const token = await getToken();
        if (!cancelled) {
          setAudioToken(token || '');
        }
      } catch (error) {
        if (!cancelled) {
          setAudioToken('');
        }
        console.error('Error preparing audio token:', error);
      }
    };

    refreshAudioToken();

    return () => {
      cancelled = true;
    };
  }, [audioFile?.isCurated, isPremiumCurated, currentUser, getToken, currentChapter?.id, audioFile?.id, curatedCatalogRoot, curatedSourceAudiobookId]);

  useEffect(() => {
    setAudioCandidateIndex(0);
  }, [audioFile?.id, currentChapter?.id, audioFile?.isCurated, userId, curatedCatalogRoot, curatedSourceAudiobookId]);

  useEffect(() => {
    const loadContent = async () => {
      if (!userId || !audioFile?.id || !currentChapter?.id) return;
      
      try {
        const transcriptCandidates = uniqueKeys([
          currentChapter?.transcriptUrl,
          currentChapter?.transcript_url,
          currentChapter?.transcriptKey,
          currentChapter?.transcript_key,
          audioFile.isCurated
            ? `${curatedCatalogRoot}/audiobooks/${curatedSourceAudiobookId}/transcripts/${currentChapter.id}.srt`
            : `users/${userId}/audiobooks/${audioFile.id}/transcripts/${currentChapter.id}.srt`
        ]);

        let transcriptLoaded = false;
        for (const transcriptKey of transcriptCandidates) {
          try {
            const srtBlob = await wasabiService.downloadFile(transcriptKey);
            const content = await srtBlob.text();
            if (!looksLikeSrt(content)) {
              console.warn(`Transcript candidate does not look like SRT, skipping key ${transcriptKey}`);
              continue;
            }
            setSrtContent(content);
            transcriptLoaded = true;
            break;
          } catch (transcriptError) {
            console.warn(`Transcript fetch failed for key ${transcriptKey}:`, transcriptError);
          }
        }
        if (!transcriptLoaded) {
          setSrtContent('');
        }

        const derivedWordTimestampCandidates = transcriptCandidates
          .filter((key) => String(key || '').toLowerCase().endsWith('.srt'))
          .map((key) => key.replace(/\.srt$/i, '.words.json'));
        const wordTimestampCandidates = uniqueKeys([
          currentChapter?.wordTimestampsUrl,
          currentChapter?.word_timestamps_url,
          currentChapter?.wordTimestampsKey,
          currentChapter?.word_timestamps_key,
          ...derivedWordTimestampCandidates,
          audioFile.isCurated
            ? `${curatedCatalogRoot}/audiobooks/${curatedSourceAudiobookId}/transcripts/${currentChapter.id}.words.json`
            : `users/${userId}/audiobooks/${audioFile.id}/transcripts/${currentChapter.id}.words.json`
        ]);

        let loadedWords = [];
        for (const wordTimestampKey of wordTimestampCandidates) {
          try {
            const wordsBlob = await wasabiService.downloadFile(wordTimestampKey);
            const rawPayload = JSON.parse(await wordsBlob.text());
            const candidateWords = Array.isArray(rawPayload)
              ? rawPayload
              : (Array.isArray(rawPayload?.words) ? rawPayload.words : []);
            if (!candidateWords.length) {
              continue;
            }
            loadedWords = candidateWords
              .map((item) => ({
                word: String(item?.word || item?.text || '').trim(),
                start_seconds: Number(item?.start_seconds ?? item?.start_time ?? item?.start ?? 0),
                end_seconds: Number(item?.end_seconds ?? item?.end_time ?? item?.end ?? 0)
              }))
              .filter((item) => (
                item.word &&
                Number.isFinite(item.start_seconds) &&
                Number.isFinite(item.end_seconds) &&
                item.end_seconds > item.start_seconds
              ));
            if (loadedWords.length) {
              break;
            }
          } catch (wordTsError) {
            console.warn(`Word-timestamp fetch failed for key ${wordTimestampKey}:`, wordTsError);
          }
        }
        setWordTimestamps(loadedWords);

        setDuration(Number(currentChapter?.duration || 0));

        const isResumeChapter = resumeChapterId && String(currentChapter.id) === String(resumeChapterId);
        const safePosition = isResumeChapter ? Math.max(0, parseFloat(resumePosition || 0)) : 0;
        setInitialChapterPosition(safePosition);
        setCurrentTime(safePosition);
      } catch (error) {
        console.error('Error loading content:', error);
      }
    };

    // Reset states when chapter changes
    setCurrentTime(0);
    setInitialChapterPosition(0);
    setSrtContent(null);
    setWordTimestamps([]);
    setDuration(0);
    
    loadContent();
  }, [
    userId,
    audioFile?.id,
    audioFile?.isCurated,
    currentChapter?.id,
    currentChapter?.duration,
    currentChapter?.transcriptUrl,
    currentChapter?.transcript_url,
    currentChapter?.transcriptKey,
    currentChapter?.transcript_key,
    currentChapter?.wordTimestampsUrl,
    currentChapter?.word_timestamps_url,
    currentChapter?.wordTimestampsKey,
    currentChapter?.word_timestamps_key,
    resumeChapterId,
    resumePosition,
    curatedCatalogRoot,
    curatedSourceAudiobookId
  ]);

  // Keep transcript viewer and current time in sync
  useEffect(() => {
    if (!audioRef.current) return;
    
    const syncTimeWithAudio = () => {
      if (!audioRef.current) return;
      const time = audioRef.current.currentTime;
      if (Math.abs(time - currentTime) > 0.1) {
        setCurrentTime(time);
      }
    };

    // Update more frequently during playback
    const interval = setInterval(syncTimeWithAudio, 100);
    
    // Also sync on timeupdate events
    const audio = audioRef.current;
    audio.addEventListener('timeupdate', syncTimeWithAudio);
    
    return () => {
      clearInterval(interval);
      audio.removeEventListener('timeupdate', syncTimeWithAudio);
    };
  }, [currentTime, showTranscript]);

  // Update buffered ranges when progress event fires
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleProgress = () => {
      const timeRanges = audio.buffered;
      const ranges = [];
      for (let i = 0; i < timeRanges.length; i++) {
        ranges.push({
          start: timeRanges.start(i),
          end: timeRanges.end(i)
        });
      }
      setBufferedRanges(ranges);
    };

    audio.addEventListener('progress', handleProgress);
    return () => audio.removeEventListener('progress', handleProgress);
  }, []);

  // Reset state when audio file or chapter changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
      setCurrentTime(0);
      setDuration(0);
      setBufferedRanges([]);
      audioRef.current.load();
    }
  }, [audioFile?.id, currentChapter?.id]);

  const handlePlayPause = async () => {
    if (!audioRef.current || isBuffering) return;

    try {
      if (isPlaying) {
        audioRef.current.pause();
        const newPlayingState = false;
        setIsPlaying(newPlayingState);
        
        // Update last played position on pause
        if (userId && audioFile?.id && currentChapter?.id && audioRef.current.currentTime > 0) {
          const pausedAt = audioRef.current.currentTime;
          saveLocalPosition(audioFile.id, { lastChapterId: currentChapter.id, lastPlayedPosition: pausedAt }, { pending: !offline.online });
          wasabiService.updateChapterPlaybackState(
            audioFile.id,
            currentChapter.id,
            { lastPlayedPosition: pausedAt }
          ).catch(error => {
            console.error('Error updating last played position on pause:', error);
            saveLocalPosition(audioFile.id, { lastChapterId: currentChapter.id, lastPlayedPosition: pausedAt }, { pending: true });
          });
        }
      } else {
        // play() must run inside the tap: iOS rejects it once the gesture
        // has passed, and waiting for the network to load the file first is
        // exactly what used to happen on the first play of a book. The
        // element starts on its own when data arrives; buffering state
        // comes from the waiting/playing events.
        if (audioRef.current.readyState < 2) {
          setIsBuffering(true);
          if (!audioRef.current.currentSrc && audioRef.current.src) audioRef.current.load();
        }

        const playPromise = audioRef.current.play();
        if (playPromise !== undefined) {
          await playPromise;
          const newPlayingState = true;
          setIsPlaying(newPlayingState);
        }
        ensureAudible();
      }
    } catch (error) {
      console.error('Playback error:', error);
      setIsPlaying(false);
      setIsBuffering(false);
    }
  };

  // iOS 26.0/26.1 (WebKit 295518): after reopening a home-screen web app,
  // play() resolves but the element stays silent. If the clock has not moved
  // 1.5 s after play, re-attach the same source at the same position once.
  const ensureAudibleTimer = useRef(null);
  const ensureAudible = () => {
    const element = audioRef.current;
    if (!element || ensureAudibleTimer.current) return;
    // Only for on-device files: a network source that has not buffered yet
    // also shows a still clock, and re-attaching it would restart the load.
    if (!String(element.currentSrc || element.src || '').startsWith('blob:')) return;
    const startedAt = element.currentTime;
    ensureAudibleTimer.current = setTimeout(() => {
      ensureAudibleTimer.current = null;
      const el = audioRef.current;
      if (!el || el.paused || el.ended) return;
      if (el.currentTime > startedAt + 0.05) return;
      if (el.readyState < 3) return; // still loading, not the silent-play bug
      const src = el.currentSrc || el.src;
      if (!src) return;
      console.warn('Audio clock stalled after play(); re-attaching source');
      const resumeAt = startedAt;
      const onMeta = () => {
        el.removeEventListener('loadedmetadata', onMeta);
        try { el.currentTime = resumeAt; } catch (e) { /* not seekable yet */ }
        el.play().catch(() => setIsPlaying(false));
      };
      el.addEventListener('loadedmetadata', onMeta);
      el.src = src;
      el.load();
    }, 1500);
  };

  // Shared by both audio elements' onEnded. Persists chapter/book completion
  // (nothing else ever marks a book finished) and fires the celebration
  // overlay when the last chapter ends.
  const handleChapterEnded = () => {
    const chapters = audioFile?.chapters || [];
    const currentIndex = chapters.findIndex(ch => ch.id === currentChapter?.id);
    const endedChapter = chapters[currentIndex] || currentChapter;
    const isLastChapter = currentIndex === -1 || currentIndex >= chapters.length - 1;
    const nextChapter = isLastChapter ? null : chapters[currentIndex + 1];
    if (isStoryTime && audioFile?.id && currentIndex >= 0) {
      storyPost('listen', { episode: currentIndex + 1, seconds: Number(endedChapter?.duration) || 0, finished: true });
      const moment = isLastChapter ? 'end' : currentIndex === 0 ? 'ep1' : '';
      if (moment && !storyAsked.current[moment]) { storyAsked.current[moment] = true; setStoryAsk(moment); }
    }

    // Sequential on purpose: the chapter update also moves the book-level
    // resume pointer, so the book-level write must land after it.
    const persistCompletion = async () => {
      if (!userId || !audioFile?.id) return;
      saveLocalPosition(audioFile.id, isLastChapter
        ? { isComplete: true, lastPlayedPosition: 0 }
        : { lastChapterId: nextChapter?.id, lastPlayedPosition: 0 }, { pending: !offline.online });
      if (endedChapter?.id) {
        try {
          await wasabiService.updateChapterPlaybackState(
            audioFile.id,
            endedChapter.id,
            { isComplete: true }
          );
        } catch (error) {
          console.error('Error marking chapter complete:', error);
        }
      }
      try {
        if (isLastChapter) {
          await wasabiService.updateAudiobookPlaybackState(audioFile.id, { isComplete: true });
        } else if (nextChapter?.id) {
          await wasabiService.updateAudiobookPlaybackState(audioFile.id, {
            lastChapterId: nextChapter.id,
            lastPlayedPosition: 0,
          });
        }
      } catch (error) {
        console.error('Error updating book playback state at chapter end:', error);
      }
    };
    persistCompletion();

    if (nextChapter && isEpisodeLocked(currentIndex + 1)) {
      // Free listener at the end of episode 1: stop here and show the note
      // instead of letting the element fail on a refused stream.
      setIsPlaying(false);
      setLockedEpisode(currentIndex + 1);
      setShowChapters(true);
      return;
    }

    if (nextChapter) {
      // "End of chapter" sleep: cue up the next chapter but stay paused.
      if (sleepModeRef.current === 'chapter') {
        setCurrentChapter(nextChapter);
        setCurrentTime(0);
        setIsPlaying(false);
        setSleepMode(null);
        setSleepEndsAt(null);
        return;
      }
      setCurrentChapter(nextChapter);
      setCurrentTime(0);
      setIsPlaying(true);
      if (audioRef.current) {
        const playNextChapter = () => {
          audioRef.current.play().catch(error => {
            console.error('Error autoplaying next chapter:', error);
            setIsPlaying(false);
          });
          audioRef.current.removeEventListener('loadedmetadata', playNextChapter);
        };
        audioRef.current.addEventListener('loadedmetadata', playNextChapter);
      }
      return;
    }

    setIsPlaying(false);
    setCurrentTime(duration);
    setShowCelebration(true);
  };

  const knowledgeOverlay = (showKnowledge && audioFile?.id) ? (
    <KnowledgePanel audiobookId={audioFile.id} onClose={() => setShowKnowledge(false)} />
  ) : null;

  const celebrationOverlay = showCelebration ? (
    <BookFinishedCelebration
      title={audioFile?.title || ''}
      coverUrl={audioFile?.coverUrl || ''}
      onSeeKnowledge={hasKnowledgeCard ? () => {
        setShowCelebration(false);
        setShowKnowledge(true);
      } : undefined}
      totalDurationSeconds={
        Number(audioFile?.totalDuration || 0) ||
        (audioFile?.chapters || []).reduce((total, ch) => total + (Number(ch?.duration) || 0), 0)
      }
      listProgress={celebrationListProgress}
      onPlayNext={onOpenCuratedBook ? (bookId) => {
        setShowCelebration(false);
        onOpenCuratedBook(bookId);
      } : undefined}
      onAddBook={() => {
        setShowCelebration(false);
        if (onAddAnotherBook) onAddAnotherBook();
      }}
      onClose={() => setShowCelebration(false)}
    />
  ) : null;

  const handleTimeUpdate = () => {
    if (!audioRef.current) return;
    const time = audioRef.current.currentTime;
    // Only update if the difference is significant to avoid unnecessary re-renders
    if (Math.abs(time - currentTime) > 0.1) {
      setCurrentTime(time);
    }
  };

  // Ensure currentTime is always synced with audio element
  useEffect(() => {
    if (audioRef.current && Math.abs(audioRef.current.currentTime - currentTime) > 0.1) {
      audioRef.current.currentTime = currentTime;
    }
  }, [currentTime]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate, audioFile?.id, currentChapter?.id]);

  // Restore this book's remembered speed when it opens. Declared BEFORE the
  // persistence effect: on mount they run in order, and persistence writing
  // the initial rate first would clobber the saved one.
  const restoredRateBookRef = useRef('');
  // True only after the user taps a speed while this book is open — the
  // per-book map is written exclusively then, so mount/StrictMode effect
  // replays can never clobber a remembered speed with the default.
  const rateTouchedRef = useRef(false);
  useEffect(() => {
    if (!audioFile?.id) return;
    restoredRateBookRef.current = String(audioFile.id);
    rateTouchedRef.current = false;
    try {
      const map = JSON.parse(localStorage.getItem(PLAYBACK_RATE_BY_BOOK_KEY) || '{}');
      const saved = Number(map[String(audioFile.id)]);
      if ([0.5, 0.75, 1.0, 1.25, 1.5, 2.0].includes(saved)) {
        setPlaybackRate(saved);
      }
    } catch (_) {
      // Corrupt map: the global rate applies.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioFile?.id]);

  useEffect(() => {
    try {
      localStorage.setItem(PLAYBACK_RATE_STORAGE_KEY, String(playbackRate));
      if (audioFile?.id && rateTouchedRef.current) {
        const map = JSON.parse(localStorage.getItem(PLAYBACK_RATE_BY_BOOK_KEY) || '{}');
        map[String(audioFile.id)] = playbackRate;
        localStorage.setItem(PLAYBACK_RATE_BY_BOOK_KEY, JSON.stringify(map));
      }
    } catch (_) {
      // Ignore storage failures (private mode, quota, etc.).
    }
  }, [playbackRate, audioFile?.id]);

  // Sleep timer bookkeeping: mirror the mode into a ref for handleChapterEnded,
  // and count down wall-clock presets, pausing when time is up.
  useEffect(() => {
    sleepModeRef.current = sleepMode;
  }, [sleepMode]);

  useEffect(() => {
    if (!sleepEndsAt) {
      setSleepRemaining(0);
      return undefined;
    }
    const tick = () => {
      const remaining = Math.max(0, Math.round((sleepEndsAt - Date.now()) / 1000));
      setSleepRemaining(remaining);
      if (remaining <= 0) {
        if (audioRef.current) audioRef.current.pause();
        setSleepMode(null);
        setSleepEndsAt(null);
      }
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [sleepEndsAt]);

  const clearSleepTimer = () => {
    setSleepMode(null);
    setSleepEndsAt(null);
    setShowSleepMenu(false);
  };

  const startSleepTimer = (mode) => {
    if (mode === 'chapter') {
      setSleepMode('chapter');
      setSleepEndsAt(null);
    } else {
      setSleepMode(mode);
      setSleepEndsAt(Date.now() + mode * 60000);
    }
    setShowSleepMenu(false);
  };

  const handleLoadedMetadata = () => {
    if (!audioRef.current) return;
    setDuration(audioRef.current.duration);
  };

  const handleSeek = (e) => {
    if (!audioRef.current) return;
    const newTime = parseFloat(e.target.value);
    
    setIsBuffering(true);
    
    // Store current state
    const wasPlaying = isPlaying;
    if (wasPlaying) {
      audioRef.current.pause();
    }

    // Set the playback position
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);

    // Resume if it was playing
    if (wasPlaying) {
      const playPromise = audioRef.current.play();
      if (playPromise !== undefined) {
        playPromise.catch(error => {
          console.error('Error resuming playback:', error);
        });
      }
    }
  };

  const handleTimeClick = (time) => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = time;
    setCurrentTime(time);
  };

  const handleForward = () => {
    if (!audioRef.current) return;
    const newTime = Math.min(audioRef.current.currentTime + 30, duration);
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleRewind = () => {
    if (!audioRef.current) return;
    const newTime = Math.max(audioRef.current.currentTime - 30, 0);
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  // Jump to the previous/next chapter (media session prev/next track).
  // Mirrors handleChapterEnded's switch: the chapter-change reset effect
  // reloads the element, and the loadedmetadata listener resumes playback.
  const goToChapterOffset = (offset) => {
    const chapters = audioFile?.chapters || [];
    if (chapters.length === 0) return;
    const currentIndex = chapters.findIndex(ch => ch.id === currentChapter?.id);
    const nextIndex = (currentIndex === -1 ? 0 : currentIndex) + offset;
    if (nextIndex < 0 || nextIndex >= chapters.length) return;
    if (isEpisodeLocked(nextIndex)) {
      setLockedEpisode(nextIndex);
      setShowChapters(true);
      return;
    }
    const wasPlaying = isPlaying;
    setCurrentChapter(chapters[nextIndex]);
    setCurrentTime(0);
    if (wasPlaying && audioRef.current) {
      const playNext = () => {
        audioRef.current.play().catch(() => setIsPlaying(false));
        audioRef.current.removeEventListener('loadedmetadata', playNext);
      };
      audioRef.current.addEventListener('loadedmetadata', playNext);
    }
  };

  // ── Media Session: lock-screen / headphone / watch controls ──────────
  // Without this, phones show a generic tab with no cover, no title, and
  // unreliable hardware controls — fatal for commute listening.
  useEffect(() => {
    if (!('mediaSession' in navigator) || !audioFile) return;
    const { title, author } = splitTitleAndAuthor(audioFile.title || 'Untitled');
    const chapters = audioFile?.chapters || [];
    const chapterIndex = chapters.findIndex(ch => ch.id === currentChapter?.id);
    const chapterLabel = currentChapter?.title
      || (chapterIndex >= 0 ? `Chapter ${chapterIndex + 1}` : '');
    try {
      navigator.mediaSession.metadata = new window.MediaMetadata({
        title: title || 'Untitled',
        artist: author || 'BYOPDF',
        album: chapterLabel,
        artwork: resolvedCoverSrc
          ? [{ src: resolvedCoverSrc, sizes: '512x512', type: 'image/png' }]
          : []
      });
    } catch (_) {
      // MediaMetadata unsupported: controls still work, just unlabeled.
    }
  }, [audioFile, currentChapter?.id, currentChapter?.title, resolvedCoverSrc]);

  useEffect(() => {
    if (!('mediaSession' in navigator)) return undefined;
    const ms = navigator.mediaSession;
    const trySet = (action, handler) => {
      try { ms.setActionHandler(action, handler); } catch (_) { /* unsupported action */ }
    };
    trySet('play', () => handlePlayPause());
    trySet('pause', () => handlePlayPause());
    trySet('seekbackward', () => handleRewind());
    trySet('seekforward', () => handleForward());
    trySet('previoustrack', () => goToChapterOffset(-1));
    trySet('nexttrack', () => goToChapterOffset(1));
    trySet('seekto', (details) => {
      if (audioRef.current && Number.isFinite(details?.seekTime)) {
        audioRef.current.currentTime = details.seekTime;
        setCurrentTime(details.seekTime);
      }
    });
    return () => {
      ['play', 'pause', 'seekbackward', 'seekforward', 'previoustrack', 'nexttrack', 'seekto']
        .forEach((action) => trySet(action, null));
    };
  });

  useEffect(() => {
    if (!('mediaSession' in navigator)) return;
    navigator.mediaSession.playbackState = isPlaying ? 'playing' : 'paused';
    if (audioRef.current && duration > 0 && typeof navigator.mediaSession.setPositionState === 'function') {
      try {
        navigator.mediaSession.setPositionState({
          duration,
          playbackRate,
          position: Math.min(audioRef.current.currentTime || 0, duration)
        });
      } catch (_) {
        // Invalid transient state (e.g. position > duration mid-switch).
      }
    }
  }, [isPlaying, duration, playbackRate, currentChapter?.id]);

  // ── Desktop keyboard shortcuts: space = play/pause, ←/→ = ±30s ──────
  useEffect(() => {
    const handleKey = (event) => {
      const target = event.target;
      const tag = String(target?.tagName || '').toUpperCase();
      if (['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON'].includes(tag) || target?.isContentEditable) {
        return;
      }
      if (event.code === 'Space') {
        event.preventDefault();
        handlePlayPause();
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        handleRewind();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        handleForward();
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  });

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  const calculateProgress = () => {
    if (!duration) return 0;
    return (currentTime / duration) * 100;
  };

  // Handle authenticated file download
  const handleDownloadChapter = async (chapter, index) => {
    try {
      if (!currentUser) {
        alert('Please sign in to download files');
        return;
      }

      // Prevent multiple simultaneous downloads
      if (downloadingChapterId !== null) {
        return;
      }

      // Set loading state
      setDownloadingChapterId(chapter.id);

      const token = await getToken();
      if (!token) {
        throw new Error('Unable to get authentication token');
      }

      const downloadName = `${audioFile.title} - Chapter ${index + 1}.mp3`;

      // Ask the backend for a short-lived signed Wasabi link. The backend records
      // the download and returns JSON; the audio bytes come straight from Wasabi
      // so they never pass through (or count against) the Render backend.
      // The chapter's audio key is not always <id>.mp3: walk the same
      // candidates the player uses and take the first one the server has.
      const candidates = chapterAudioKeyCandidates(audioFile, chapter, userId);
      let signedUrl = '';
      let lastFailure = '';
      for (const fileKey of candidates) {
        const url = getApiUrl(
          `/api/wasabi/file/${encodeURIComponent(fileKey)}?download=true&filename=${encodeURIComponent(downloadName)}`
        );
        const response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        if (response.status === 404) { lastFailure = 'file not found'; continue; }
        if (!response.ok) {
          let detail = '';
          try {
            const body = await response.text();
            try { detail = JSON.parse(body)?.detail || body; } catch (e) { detail = body; }
          } catch (e) { detail = ''; }
          lastFailure = `${response.status} ${String(detail || response.statusText).slice(0, 160)}`;
          break;
        }
        const payload = await response.json();
        signedUrl = payload?.url || '';
        if (signedUrl) break;
        lastFailure = 'no download link returned';
      }
      if (!signedUrl) {
        throw new Error(lastFailure || 'file not found');
      }

      // The signed URL carries Content-Disposition: attachment, so navigating
      // to it saves the file. Inside an installed iOS app that navigation would
      // trap the user in a full-screen preview, so open it in a Safari view
      // (has a Done button) instead.
      if (isIOS() && offline.standalone) {
        const opened = window.open(signedUrl, '_blank', 'noopener');
        if (!opened) window.location.assign(signedUrl);
      } else {
        const link = document.createElement('a');
        link.href = signedUrl;
        link.rel = 'noopener';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Error downloading chapter:', error);
      labLog('chapter export error', { book: audioFile?.title, chapter: chapter?.id, message: String(error?.message || error) });
      alert(`Could not download this chapter (${error.message}). Try again in a moment, or use Download on the book card in the BYOPDF app for offline listening.`);
    } finally {
      // Clear loading state
      setDownloadingChapterId(null);
    }
  };

  // Update last played position periodically
  useEffect(() => {
    // Only set up interval when playing
    if (!isPlaying) return;

    const updateInterval = setInterval(async () => {
      if (!audioRef.current || !userId || !audioFile?.id || !currentChapter?.id) return;

      const currentPosition = audioRef.current.currentTime;
      if (currentPosition <= 0) return;

      saveLocalPosition(audioFile.id, { lastChapterId: currentChapter.id, lastPlayedPosition: currentPosition }, { pending: !offline.online });
      try {
        await wasabiService.updateChapterPlaybackState(
          audioFile.id,
          currentChapter.id,
          {
            lastPlayedPosition: currentPosition
          }
        );
      } catch (error) {
        console.error('Error updating last played position in interval:', error);
        saveLocalPosition(audioFile.id, { lastChapterId: currentChapter.id, lastPlayedPosition: currentPosition }, { pending: true });
      }
    }, 30000); // Update every 30 seconds

    return () => clearInterval(updateInterval);
  }, [userId, audioFile?.id, currentChapter?.id, audioFile?.isCurated, isPlaying, offline.online]);

  // Cleanup background music on unmount
  useEffect(() => {
    return () => {
      if (bgMusicRef.current) {
        bgMusicRef.current.pause();
        bgMusicRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!showMusicMenu || typeof document === 'undefined') return undefined;

    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setShowMusicMenu(false);
      }
    };

    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showMusicMenu]);

  // Escape closes the Now Playing sheet (desktop theater especially).
  useEffect(() => {
    if (!isExpanded || typeof document === 'undefined') return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') setIsExpanded(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isExpanded]);

  // When the celebration opens, look up whether this book advances a reading
  // list so the modal can show ladder progress and queue the next book.
  useEffect(() => {
    if (!showCelebration) {
      setCelebrationListProgress(null);
      return undefined;
    }
    if (!currentUser || !audioFile?.id) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/gamification`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        const candidateIds = new Set(
          [audioFile.id, audioFile.sourceAudiobookId].filter(Boolean).map(String)
        );
        const collection = (data.collections || []).find((c) =>
          (c.books || []).some((b) => candidateIds.has(String(b.id)))
        );
        if (!collection || cancelled) return;
        // The server may not have registered this finish yet — count it locally.
        const books = (collection.books || []).map((b) =>
          candidateIds.has(String(b.id)) ? { ...b, finished: true } : b
        );
        const finishedCount = books.filter((b) => b.finished).length;
        const nextBook = books.find((b) => !b.finished) || null;
        setCelebrationListProgress({
          listTitle: collection.title,
          finishedCount,
          total: books.length,
          levelTitle: collection.levelTitle || null,
          nextBook: nextBook ? { id: nextBook.id, title: nextBook.title } : null
        });
      } catch (_) {
        // The celebration is complete without list context.
      }
    })();
    return () => { cancelled = true; };
  }, [showCelebration, currentUser, audioFile?.id, audioFile?.sourceAudiobookId, getToken]);

  // Check if we have valid audio data
  if (!audioFile || !audioFile.id) {
    return <div>Loading audio...</div>;
  }

  // Ensure chapters is an array
  if (!Array.isArray(audioFile.chapters)) {
    audioFile.chapters = [audioFile.chapters].filter(Boolean);
  }

  // Initialize chapters if empty
  if (!audioFile.chapters?.length) {
    audioFile.chapters = [{ id: '1', title: 'Chapter 1' }];
  }

  // Set current chapter if not set
  if (!currentChapter) {
    setCurrentChapter(audioFile.chapters[0]);
    return <div>Loading audio...</div>;
  }

  const chapterId = currentChapter?.id || '1';
  const currentChapterIndex = Math.max(
    0,
    audioFile.chapters.findIndex(ch => String(ch?.id) === String(chapterId))
  );
  const defaultAudioBase = audioFile.isCurated
    ? `${curatedCatalogRoot}/audiobooks/${curatedSourceAudiobookId}/audio/${chapterId}`
    : `users/${userId}/audiobooks/${audioFile.id}/audio/${chapterId}`;

  const chapterAudioCandidates = uniqueKeys([
    currentChapter?.audioUrl,
    currentChapter?.audio_url,
    currentChapter?.audioKey,
    currentChapter?.audio_key
  ]).filter(key => isKeyForCurrentAudiobook(key, {
    isCurated: Boolean(audioFile.isCurated),
    userId,
    audiobookId: audioFile.id,
    sourceAudiobookId: curatedSourceAudiobookId,
    catalogRoot: curatedCatalogRoot
  }));

  const audioKeyCandidates = uniqueKeys([
    ...chapterAudioCandidates,
    ...AUDIO_FILE_EXTENSIONS.map(extension => `${defaultAudioBase}.${extension}`)
  ]);

  const rawAudioKey = audioKeyCandidates[audioCandidateIndex] || '';

  // Use redirect endpoint so large audio is streamed directly from Wasabi (not proxied through backend memory).
  const networkAudioSrc = audioFile.isCurated
    ? (rawAudioKey
      ? getApiUrl(`/api/wasabi/stream/${encodeURIComponent(rawAudioKey)}${
        isPremiumCurated && audioToken ? `?authToken=${encodeURIComponent(audioToken)}` : ''
      }`)
      : '')
    : (rawAudioKey && audioToken
      ? getApiUrl(`/api/wasabi/stream/${encodeURIComponent(rawAudioKey)}?authToken=${encodeURIComponent(audioToken)}`)
      : '');
  // A downloaded chapter always plays from disk; while its blob URL is being
  // prepared, do not start a network load that would be thrown away.
  const audioSrc = offlineSrc || (offlineChapterReady ? '' : networkAudioSrc);

  // Handle background music menu toggle
  const toggleMusicMenu = () => {
    setShowMusicMenu(!showMusicMenu);
  };

  const handleToggleCompact = () => {
    setIsCompact((previous) => !previous);
  };

  // Handle music track selection
  const selectMusicTrack = async (trackIndex) => {
    if (!bgMusicRef.current) {
      bgMusicRef.current = new Audio();
      bgMusicRef.current.loop = true;
      bgMusicRef.current.volume = bgMusicVolume;
    }

    // If selecting the same track that's playing, stop it
    if (trackIndex === currentMusicTrack && isMusicPlaying) {
      bgMusicRef.current.pause();
      bgMusicRef.current.currentTime = 0;
      setIsMusicPlaying(false);
      setCurrentMusicTrack(-1);
    } else {
      // Play the selected track
      setCurrentMusicTrack(trackIndex);
      bgMusicRef.current.src = bgMusicTracks[trackIndex].file;
      await bgMusicRef.current.play();
      setIsMusicPlaying(true);
    }
    
    setShowMusicMenu(false);
  };

  // Handle background music volume change
  const handleBgVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setBgMusicVolume(newVolume);
    if (bgMusicRef.current) {
      bgMusicRef.current.volume = newVolume;
    }
  };

  // Stop background music
  const stopBackgroundMusic = () => {
    if (bgMusicRef.current) {
      bgMusicRef.current.pause();
      bgMusicRef.current.currentTime = 0;
    }
    setIsMusicPlaying(false);
    setCurrentMusicTrack(-1);
    setShowMusicMenu(false);
  };

  const musicMenuPortal =
    showMusicMenu && !isCinematicAudio && typeof document !== 'undefined'
      ? createPortal(
          <>
            <button
              className={styles.musicMenuOverlay}
              onClick={() => setShowMusicMenu(false)}
              aria-label="Close music menu"
            />
            <div className={styles.musicMenu} role="dialog" aria-modal="true" aria-label="Background music selection">
              <div className={styles.musicMenuHeader}>
                <h4 className={styles.musicMenuTitle}>Background Music</h4>
                <button
                  className={styles.musicMenuClose}
                  onClick={() => setShowMusicMenu(false)}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              
              {/* Volume Control */}
              <div className={styles.volumeControl}>
                <label className={styles.volumeLabel}>
                  <span>Volume: {Math.round(bgMusicVolume * 100)}%</span>
                </label>
                <input
                  type="range"
                  min="0"
                  max="0.3"
                  step="0.01"
                  value={bgMusicVolume}
                  onChange={handleBgVolumeChange}
                  className={styles.volumeSlider}
                />
              </div>

              <div className={styles.musicTracks}>
                {isMusicPlaying && (
                  <button
                    className={styles.stopMusicButton}
                    onClick={stopBackgroundMusic}
                  >
                    Stop Music
                  </button>
                )}
                {bgMusicTracks.map((track, index) => (
                  <button
                    key={index}
                    className={`${styles.musicTrack} ${index === currentMusicTrack && isMusicPlaying ? styles.activeMusicTrack : ''}`}
                    onClick={() => selectMusicTrack(index)}
                  >
                    {track.name}
                    {index === currentMusicTrack && isMusicPlaying && (
                      <span className={styles.playingIndicator}>♪</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </>,
          document.body
        )
      : null;

  const handleExpandMobile = () => {
    setShowTranscript(false);
    setIsExpanded(true);
  };

  const openReadAlongMobile = () => {
    setIsExpanded(false);
    setShowTranscript(true);
  };

  // Shared blocks used by both the desktop card and the mobile sheet.
  const renderChapterList = () => (
    <div className={styles.chapterList}>
      <h4 className={styles.chapterListTitle}>{isStoryTime ? 'Episodes' : 'Chapters'}</h4>
      {isStoryTime && storyAsk && <StoryVerdict key={storyAsk} moment={storyAsk} send={(verdict, reasons) => storyPost('feedback', { moment: storyAsk, verdict, reasons })} onDone={() => setStoryAsk('')} />}
      {isStoryTime && <StoryShare jobId={audioFile.id} />}
      {lockedEpisode !== null && isEpisodeLocked(lockedEpisode) && (
        <div className={styles.premiumNote} role="status">
          <RiLockLine size={16} aria-hidden="true" />
          <div className={styles.premiumNoteBody}>
            <strong>Episode {lockedEpisode + 1} is for subscribers.</strong>
            <span>The first episode of every Premium series is free. A subscription opens the rest, and every other series on the shelf.</span>
            <a className={styles.premiumNoteLink} href="/settings">See plans</a>
          </div>
          <button
            type="button"
            className={styles.premiumNoteClose}
            onClick={() => setLockedEpisode(null)}
            aria-label="Dismiss"
          >
            <RiCloseLine size={16} />
          </button>
        </div>
      )}
      <div className={styles.chapters}>
        {audioFile.chapters.map((chapter, index) => (
          <div
            key={chapter.id}
            className={`${styles.chapterItem} ${chapter.id === currentChapter?.id ? styles.active : ''} ${isEpisodeLocked(index) ? styles.chapterLocked : ''}`}
          >
            <button
              className={styles.chapterButton}
              aria-disabled={isEpisodeLocked(index) ? 'true' : undefined}
              onClick={() => {
                if (isEpisodeLocked(index)) {
                  setLockedEpisode(index);
                  return;
                }
                setCurrentChapter(chapter);
                setShowChapters(false);
                setIsPlaying(false);
                setCurrentTime(0);
              }}
            >
              <div className={styles.chapterNumber}>
                {isEpisodeLocked(index) && <RiLockLine size={13} className={styles.chapterLockIcon} aria-label="Subscribers only" />}
                {chapter?.title || `Chapter ${index + 1}`}
              </div>
              <div className={styles.chapterDuration}>{chapter.duration ? formatTime(chapter.duration) : '--:--'}</div>
            </button>
            {!audioFile.isCurated && !isStoryTime && (
              <button
                className={styles.downloadButton}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownloadChapter(chapter, index);
                }}
                disabled={downloadingChapterId !== null}
                title={downloadingChapterId === chapter.id ? "Downloading..." : downloadingChapterId !== null ? "Another download in progress" : "Download chapter"}
              >
                {downloadingChapterId === chapter.id ? (
                  <div className={styles.downloadSpinner}>
                    <svg width="16" height="16" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" fill="none" opacity="0.25"/>
                      <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" fill="none" strokeLinecap="round"/>
                    </svg>
                  </div>
                ) : (
                  '⬇️'
                )}
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const renderProgressBar = () => (
    <div className={styles.progressBar}>
      {bufferedRanges.map((range, index) => (
        <div
          key={index}
          className={styles.bufferedRange}
          style={{
            left: `${(range.start / duration) * 100}%`,
            width: `${((range.end - range.start) / duration) * 100}%`
          }}
        />
      ))}
      <div
        className={styles.progressBarFill}
        style={{ width: `${calculateProgress()}%` }}
      />
      <input
        type="range"
        min="0"
        max={duration || 0}
        value={currentTime}
        onChange={handleSeek}
        className={styles.progressInput}
      />
      {isBuffering && <div className={styles.bufferingIndicator} />}
    </div>
  );

  const bufferingSpinner = (size) => (
    <div className={styles.loadingSpinner}>
      <svg width={size} height={size} viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" fill="none"/>
      </svg>
    </div>
  );

  const coverSrc = !miniCoverFailed ? resolvedCoverSrc : '';
  const { title: coverTitle, author: coverAuthor } = splitTitleAndAuthor(audioFile.title);

  // Now Playing sheet, shared by mobile (full-screen slide-up) and desktop
  // (centered "theater" card over a dimmed backdrop — see module CSS).
  const closeExpanded = () => setIsExpanded(false);
  const expandedSheet = (
    <div
      className={styles.expandedSheet}
      role="dialog"
      aria-modal="true"
      aria-label="Now playing"
      onClick={(e) => {
        // Desktop: the root doubles as the dimmed backdrop.
        if (e.target === e.currentTarget) closeExpanded();
      }}
    >
      <div className={styles.sheetCard}>
        <div className={styles.sheetHeader}>
          <button
            className={styles.sheetIconButton}
            onClick={closeExpanded}
            aria-label="Minimize player"
          >
            <RiArrowDownSLine size={26} />
          </button>
          <span className={styles.sheetHeaderLabel}>Now Playing</span>
          <span className={styles.sheetHeaderActions}>
            {isStoryTime && <StoryShareButton jobId={audioFile.id} className={styles.sheetIconButton} />}
            {hasKnowledgeCard && (
              <button
                className={styles.sheetIconButton}
                onClick={() => setShowKnowledge(true)}
                title="What this book gives you"
              >
                <RiLightbulbLine size={22} />
              </button>
            )}
            <button
              className={styles.sheetIconButton}
              onClick={openReadAlongMobile}
              title="Read along"
              data-player-tour="read-along-button"
            >
              <RiFileTextLine size={22} />
            </button>
          </span>
        </div>

        <div className={styles.sheetBody}>
          {coverSrc ? (
            <img
              className={styles.sheetCover}
              src={coverSrc}
              alt=""
              onError={() => setMiniCoverFailed(true)}
            />
          ) : (
            <div className={styles.sheetCoverFallback}>
              <AutoBookCover variant="large" title={coverTitle} author={coverAuthor} seed={coverTitle} />
            </div>
          )}

          <h3 className={styles.sheetTitle}>{coverTitle}</h3>
          {coverAuthor && <p className={styles.sheetAuthor}>{coverAuthor}</p>}
          <button
            className={styles.chapterSelect}
            onClick={() => setShowChapters(!showChapters)}
            data-player-tour="chapters-button"
          >
            Chapter {currentChapterIndex + 1} of {audioFile.chapters.length} · {formatTime(duration)} total
          </button>

          <div className={styles.sheetProgressBlock}>
            {renderProgressBar()}
            <div className={styles.timeDisplay}>
              <span>{formatTime(currentTime)}</span>
              <span>-{formatTime(duration - currentTime)}</span>
            </div>
          </div>

          <div className={styles.sheetMainControls} data-player-tour="playback-controls">
            <button onClick={handleRewind} className={styles.controlButton} title="Rewind 30 seconds">
              <MdReplay30 size={32} />
            </button>
            <button
              onClick={handlePlayPause}
              className={`${styles.playButton} ${styles.sheetPlayButton}`}
              disabled={isBuffering}
              aria-label={isPlaying ? 'Pause' : 'Play'}
            >
              {isBuffering ? bufferingSpinner(30) : isPlaying ? <RiPauseFill size={34} /> : <RiPlayFill size={34} />}
            </button>
            <button onClick={handleForward} className={styles.controlButton} title="Forward 30 seconds">
              <MdForward30 size={32} />
            </button>
          </div>

          <div className={styles.sheetSecondaryRow}>
            <button
              onClick={() => setShowSpeedControls(!showSpeedControls)}
              className={`${styles.controlButton} ${showSpeedControls ? styles.active : ''}`}
              title="Playback speed"
              data-player-tour="speed-button"
            >
              <span className={styles.speedText}>{playbackRate}x</span>
            </button>
            {!isCinematicAudio && (
              <button
                onClick={toggleMusicMenu}
                className={`${styles.controlButton} ${isMusicPlaying ? styles.active : ''}`}
                title="Background music"
                data-player-tour="music-button"
              >
                <RiMusicLine size={24} />
              </button>
            )}
            <button
              onClick={() => setShowSleepMenu(!showSleepMenu)}
              className={`${styles.controlButton} ${sleepMode ? styles.active : ''}`}
              title={
                sleepMode === 'chapter'
                  ? 'Sleep: stopping at the end of this chapter'
                  : sleepMode
                    ? `Sleep in ${Math.max(1, Math.ceil(sleepRemaining / 60))} min`
                    : 'Sleep timer'
              }
              aria-label="Sleep timer"
            >
              <RiMoonLine size={22} aria-hidden="true" />
              {sleepMode && (
                <span className={styles.speedText}>
                  {sleepMode === 'chapter' ? 'Ch' : `${Math.max(1, Math.ceil(sleepRemaining / 60))}m`}
                </span>
              )}
            </button>
          </div>

          {showSleepMenu && (
            <div className={styles.sheetSpeedRow}>
              {SLEEP_PRESETS_MINUTES.map((minutes) => (
                <button
                  key={minutes}
                  onClick={() => startSleepTimer(minutes)}
                  className={`${styles.speedButton} ${sleepMode === minutes ? styles.active : ''}`}
                >
                  {minutes}m
                </button>
              ))}
              <button
                onClick={() => startSleepTimer('chapter')}
                className={`${styles.speedButton} ${sleepMode === 'chapter' ? styles.active : ''}`}
              >
                End of chapter
              </button>
              {sleepMode && (
                <button onClick={clearSleepTimer} className={styles.speedButton}>
                  Off
                </button>
              )}
            </div>
          )}

          {showSpeedControls && (
            <div className={styles.sheetSpeedRow}>
              {[0.5, 0.75, 1.0, 1.25, 1.5, 2.0].map(speed => (
                <button
                  key={speed}
                  onClick={() => {
                    if (audioRef.current) {
                      audioRef.current.playbackRate = speed;
                      rateTouchedRef.current = true;
                      setPlaybackRate(speed);
                      setShowSpeedControls(false);
                    }
                  }}
                  className={`${styles.speedButton} ${playbackRate === speed ? styles.active : ''}`}
                >
                  {speed}x
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {showChapters && (
        <>
          <button
            className={styles.sheetChaptersBackdrop}
            onClick={() => setShowChapters(false)}
            aria-label="Close chapter list"
          />
          <div className={styles.sheetChaptersOverlay}>
            {renderChapterList()}
          </div>
        </>
      )}
    </div>
  );

  const mobilePlayer = (
    <>
      {!isExpanded && (
        <div
          ref={playerContainerRef}
          className={styles.miniPlayer}
          style={{ bottom: navClearance }}
          role="button"
          tabIndex={0}
          aria-label="Expand player"
          onClick={handleExpandMobile}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleExpandMobile();
            }
          }}
        >
          <div className={styles.miniProgressTrack}>
            <div className={styles.miniProgressFill} style={{ width: `${calculateProgress()}%` }} />
          </div>
          {coverSrc ? (
            <img
              className={styles.miniCover}
              src={coverSrc}
              alt=""
              onError={() => setMiniCoverFailed(true)}
            />
          ) : (
            <div className={styles.miniCoverFallback}>
              <AutoBookCover variant="mini" title={coverTitle} seed={coverTitle} />
            </div>
          )}
          <div className={styles.miniMeta}>
            <span className={styles.miniTitle}>{coverTitle}</span>
            <span className={styles.miniSubtitle}>
              Chapter {currentChapterIndex + 1} of {audioFile.chapters.length} · {formatTime(currentTime)} / {formatTime(duration)}
            </span>
          </div>
          <div
            className={styles.miniControls}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <button className={styles.miniControlButton} onClick={handleRewind} title="Rewind 30 seconds">
              <MdReplay30 size={22} />
            </button>
            <button
              className={styles.miniPlayButton}
              onClick={handlePlayPause}
              disabled={isBuffering}
              aria-label={isPlaying ? 'Pause' : 'Play'}
            >
              {isBuffering ? bufferingSpinner(18) : isPlaying ? <RiPauseFill size={22} /> : <RiPlayFill size={22} />}
            </button>
            <button className={styles.miniControlButton} onClick={handleForward} title="Forward 30 seconds">
              <MdForward30 size={22} />
            </button>
            {!isPlaying && onClose && (
              <button
                className={styles.miniCloseButton}
                onClick={(e) => {
                  e.stopPropagation();
                  if (audioRef.current) audioRef.current.pause();
                  onClose();
                }}
                aria-label="Close player"
                title="Close player"
              >
                <RiCloseLine size={18} />
              </button>
            )}
          </div>
        </div>
      )}

      {isExpanded && expandedSheet}

      {musicMenuPortal}
    </>
  );

  // Render through a portal to <body>: the app's route wrapper animates
  // `transform`, which turns position:fixed descendants into page-relative
  // elements (the player then scrolls with the page, especially on mobile
  // WebKit). Escaping the wrapper pins the player to the viewport for real.
  if (isMobile) {
    return createPortal(
      <>
        <SRTViewer
          srtContent={srtContent}
          wordTimestamps={wordTimestamps}
          currentTime={currentTime}
          duration={duration}
          isVisible={showTranscript && !isExpanded}
          playerClearanceOverride={transcriptPlayerClearance}
          onToggle={() => setShowTranscript(false)}
        />
        {mobilePlayer}
        <audio
          ref={audioRef}
          src={audioSrc || undefined}
          onTimeUpdate={handleTimeUpdate}
          onLoadStart={() => setIsBuffering(true)}
          onLoadedMetadata={(e) => {
            if (e.target.duration && !isNaN(e.target.duration)) {
              setDuration(e.target.duration);
            }
            e.target.playbackRate = playbackRate;
            if (audioRef.current) {
              const lastPosition = initialChapterPosition || 0;
              if (lastPosition > 0) {
                audioRef.current.currentTime = lastPosition;
                setCurrentTime(lastPosition);
              }
            }
          }}
          onLoadedData={() => setIsBuffering(false)}
          onWaiting={() => setIsBuffering(true)}
          onCanPlay={() => setIsBuffering(false)}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onPlaying={() => {
            setIsBuffering(false);
            setIsPlaying(true);
            if (audioRef.current) {
              audioRef.current.playbackRate = playbackRate;
              setCurrentTime(audioRef.current.currentTime);
            }
          }}
          onSeeking={() => {
            setIsBuffering(true);
            if (audioRef.current) {
              setCurrentTime(audioRef.current.currentTime);
            }
          }}
          onSeeked={() => {
            setIsBuffering(false);
            if (audioRef.current) {
              setCurrentTime(audioRef.current.currentTime);
            }
          }}
          onEnded={handleChapterEnded}
          onError={(e) => {
            // Only a failure of the key we are showing now moves to the next
            // guess. An empty src (own books wait for the auth token) or a
            // late error from the previous chapter must not skip the .mp3.
            if (!audioSrc || e.target.getAttribute('src') !== audioSrc) return;
            const hasAnotherCandidate = audioCandidateIndex < (audioKeyCandidates.length - 1);
            if (hasAnotherCandidate) {
              setAudioCandidateIndex(audioCandidateIndex + 1);
              return;
            }
            console.error('Audio playback error:', {
              mediaErrorCode: e.target.error?.code,
              mediaErrorMessage: e.target.error?.message,
              source: e.target.src
            });
            setIsBuffering(false);
          }}
          preload="auto"
        />
        <Suspense fallback={null}><AudioPlayerTutorial /></Suspense>
        {celebrationOverlay}
        {knowledgeOverlay}
      </>,
      document.body
    );
  }

  return createPortal(
    <>
      <SRTViewer
        srtContent={srtContent}
        wordTimestamps={wordTimestamps}
        currentTime={currentTime}
        duration={duration}
        isVisible={showTranscript && !isCompact && !isExpanded}
        playerClearanceOverride={transcriptPlayerClearance}
        onToggle={() => setShowTranscript(false)}
      />

      {isExpanded && expandedSheet}

      <div
        ref={playerContainerRef}
        className={`${styles.audioPlayer} ${isCompact ? styles.compact : ''}`}
      >
        <div className={styles.playerInfo}>
          <button
            type="button"
            className={styles.barCoverButton}
            onClick={handleExpandMobile}
            title="Open Now Playing"
            aria-label="Open Now Playing"
          >
            {coverSrc ? (
              <img className={styles.barCover} src={coverSrc} alt="" onError={() => setMiniCoverFailed(true)} />
            ) : (
              <span className={styles.barCoverFallback} aria-hidden="true">
                <AutoBookCover variant="mini" title={coverTitle} seed={coverTitle} />
              </span>
            )}
          </button>
          <div className={styles.titleContainer}>
            <h3
              className={`${styles.title} ${styles.titleClickable}`}
              onClick={handleExpandMobile}
              title="Open Now Playing"
            >
              {coverTitle || 'Untitled'}
            </h3>
            {!isCompact && (
              <div className={styles.chapterInfo}>
                <p className={styles.duration}>{formatTime(duration)} total length</p>
                <button
                  className={styles.chapterSelect}
                  onClick={() => setShowChapters(!showChapters)}
                  data-player-tour="chapters-button"
                >
                  Chapter {currentChapterIndex + 1} of {audioFile.chapters.length}
                </button>
              </div>
            )}
          </div>
          <div className={styles.playerActions}>
            {!isCompact && hasKnowledgeCard && (
              <button
                className={`${styles.transcriptButton} ${showKnowledge ? styles.active : ''}`}
                onClick={() => setShowKnowledge(true)}
                title="What this book gives you"
              >
                <RiLightbulbLine size={20} />
              </button>
            )}
            {!isCompact && isStoryTime && (
              <StoryShareButton jobId={audioFile.id} className={styles.transcriptButton} size={20} />
            )}
            {!isCompact && (
              <button
                className={`${styles.transcriptButton} ${showTranscript ? styles.active : ''}`}
                onClick={() => setShowTranscript(!showTranscript)}
                title="Toggle transcript"
                data-player-tour="read-along-button"
              >
                <RiFileTextLine size={20} />
              </button>
            )}
            {isCompact && (
              <button
                onClick={handleToggleCompact}
                className={`${styles.controlButton} ${styles.compactToggleIcon}`}
                title="Expand player"
              >
                <RiArrowUpSLine size={22} />
              </button>
            )}
          </div>
        </div>

        {!isCompact && showChapters && renderChapterList()}

        {renderProgressBar()}

        {!isCompact && (
          <div className={styles.timeDisplay}>
            <span>{formatTime(currentTime)}</span>
            <span>-{formatTime(duration - currentTime)}</span>
          </div>
        )}

        <div className={`${styles.controls} ${isCompact ? styles.compactControls : ''}`}>
          <div className={`${styles.mainControls} ${isCompact ? styles.compactMainControls : ''}`} data-player-tour="playback-controls">
            <button
              onClick={handleRewind}
              className={styles.controlButton}
              title="Rewind 30 seconds"
            >
              <MdReplay30 size={isCompact ? 20 : 24} />
            </button>

            <button
              onClick={handlePlayPause}
              className={styles.playButton}
              disabled={isBuffering}
            >
              {isBuffering ? (
                <div className={styles.loadingSpinner}>
                  <svg width="28" height="28" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" fill="none"/>
                  </svg>
                </div>
              ) : isPlaying ? (
                <RiPauseFill size={isCompact ? 24 : 28} />
              ) : (
                <RiPlayFill size={isCompact ? 24 : 28} />
              )}
            </button>

            <button
              onClick={handleForward}
              className={styles.controlButton}
              title="Forward 30 seconds"
            >
              <MdForward30 size={isCompact ? 20 : 24} />
            </button>
          </div>

          {isCompact ? (
            <span className={styles.compactTime}>{formatTime(currentTime)} / {formatTime(duration)}</span>
          ) : (
            <button
              onClick={() => {
                setShowSleepMenu(false);
                setShowSpeedControls(!showSpeedControls);
              }}
              className={`${styles.controlButton} ${showSpeedControls ? styles.active : ''}`}
              title="Playback speed"
              data-player-tour="speed-button"
            >
              <span className={styles.speedText}>{playbackRate}x</span>
            </button>
          )}

          {!isCompact && (
            <button
              onClick={handleToggleCompact}
              className={`${styles.controlButton} ${styles.compactControlButton}`}
              title="Minimize player"
            >
              <RiArrowDownSLine size={24} />
            </button>
          )}

          {!isCompact && (
            <div className={styles.rightControls}>
              <button
                onClick={() => {
                  setShowSpeedControls(false);
                  setShowSleepMenu(!showSleepMenu);
                }}
                className={`${styles.controlButton} ${sleepMode ? styles.active : ''}`}
                title={
                  sleepMode === 'chapter'
                    ? 'Sleep: stopping at the end of this chapter'
                    : sleepMode
                      ? `Sleep in ${Math.max(1, Math.ceil(sleepRemaining / 60))} min`
                      : 'Sleep timer'
                }
                aria-label="Sleep timer"
              >
                <RiMoonLine size={22} aria-hidden="true" />
                {sleepMode && (
                  <span className={styles.speedText}>
                    {sleepMode === 'chapter' ? 'Ch' : `${Math.max(1, Math.ceil(sleepRemaining / 60))}m`}
                  </span>
                )}
              </button>
              {!isCinematicAudio && (
                <button
                  onClick={toggleMusicMenu}
                  className={`${styles.controlButton} ${isMusicPlaying ? styles.active : ''}`}
                  title="Background music"
                  data-player-tour="music-button"
                >
                  <RiMusicLine size={24} />
                </button>
              )}
            </div>
          )}

          {!isCompact && musicMenuPortal}


          {!isCompact && showSpeedControls && (
            <div className={styles.speedControls}>
              {[0.5, 0.75, 1.0, 1.25, 1.5, 2.0].map(speed => (
                <button
                  key={speed}
                  onClick={() => {
                    if (audioRef.current) {
                      audioRef.current.playbackRate = speed;
                      rateTouchedRef.current = true;
                      setPlaybackRate(speed);
                      setShowSpeedControls(false);
                    }
                  }}
                  className={`${styles.speedButton} ${playbackRate === speed ? styles.active : ''}`}
                >
                  {speed}x
                </button>
              ))}
            </div>
          )}

          {!isCompact && showSleepMenu && (
            <div className={styles.speedControls}>
              {SLEEP_PRESETS_MINUTES.map((minutes) => (
                <button
                  key={minutes}
                  onClick={() => startSleepTimer(minutes)}
                  className={`${styles.speedButton} ${sleepMode === minutes ? styles.active : ''}`}
                >
                  {minutes}m
                </button>
              ))}
              <button
                onClick={() => startSleepTimer('chapter')}
                className={`${styles.speedButton} ${sleepMode === 'chapter' ? styles.active : ''}`}
              >
                End of chapter
              </button>
              {sleepMode && (
                <button onClick={clearSleepTimer} className={styles.speedButton}>
                  Off
                </button>
              )}
            </div>
          )}
        </div>

        <audio
          ref={audioRef}
          src={audioSrc || undefined}
          onTimeUpdate={handleTimeUpdate}
          onLoadStart={() => setIsBuffering(true)}
          onLoadedMetadata={(e) => {
            if (e.target.duration && !isNaN(e.target.duration)) {
              setDuration(e.target.duration);
            }

            e.target.playbackRate = playbackRate;

            if (audioRef.current) {
              const lastPosition = initialChapterPosition || 0;
              if (lastPosition > 0) {
                audioRef.current.currentTime = lastPosition;
                setCurrentTime(lastPosition);
              }
            }
          }}
          onLoadedData={() => setIsBuffering(false)}
          onWaiting={() => setIsBuffering(true)}
          onCanPlay={() => setIsBuffering(false)}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onPlaying={() => {
            setIsBuffering(false);
            setIsPlaying(true);
            if (audioRef.current) {
              audioRef.current.playbackRate = playbackRate;
              setCurrentTime(audioRef.current.currentTime);
            }
          }}
          onSeeking={() => {
            setIsBuffering(true);
            if (audioRef.current) {
              setCurrentTime(audioRef.current.currentTime);
            }
          }}
          onSeeked={() => {
            setIsBuffering(false);
            if (audioRef.current) {
              const time = audioRef.current.currentTime;
              setCurrentTime(time);
            }
          }}
          onEnded={handleChapterEnded}
          onError={(e) => {
            // Only a failure of the key we are showing now moves to the next
            // guess. An empty src (own books wait for the auth token) or a
            // late error from the previous chapter must not skip the .mp3.
            if (!audioSrc || e.target.getAttribute('src') !== audioSrc) return;
            const hasAnotherCandidate = audioCandidateIndex < (audioKeyCandidates.length - 1);
            if (hasAnotherCandidate) {
              const nextIndex = audioCandidateIndex + 1;
              console.warn('Audio source failed, trying fallback key:', {
                failedKey: audioKeyCandidates[audioCandidateIndex],
                nextKey: audioKeyCandidates[nextIndex]
              });
              setAudioCandidateIndex(nextIndex);
              return;
            }

            console.error('Audio playback error:', {
              error: e,
              mediaError: e.target.error,
              mediaErrorCode: e.target.error?.code,
              mediaErrorMessage: e.target.error?.message,
              source: e.target.src,
              readyState: e.target.readyState,
              networkState: e.target.networkState,
              currentSrc: e.target.currentSrc
            });
            setIsBuffering(false);
          }}
          preload="auto"
        />
      </div>

      <Suspense fallback={null}><AudioPlayerTutorial /></Suspense>
      {celebrationOverlay}
        {knowledgeOverlay}
    </>,
    document.body
  );
}

export default AudioPlayer;
